

export const dynamic = "force-dynamic";

import { useState, useEffect, useMemo } from "react";
import { eventsApi, mastersApi } from "@/lib/api";
import DashboardLayout from "@/app/components/DashboardLayout";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  List,
  Grid3X3,
  Filter,
  X,
  Search,
  MapPin,
  Users,
  CalendarDays,
  Clock,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import AvailabilityChecker from "./AvailabilityChecker";
import AddEventModal from "@/app/events/components/AddEventModal";

interface Event {
  id: number;
  name: string;
  event_name?: string;
  start_date: string;
  end_date?: string | null;
  event_date?: string;
  client_id: number;
  client?: { id: number; name: string };
  venue_id?: number;
  venue?: { id: number; name: string; location: string };
  status: string;
  total_amount: number;
  event_type?: string;
  type?: string;
  guest_count?: number;
}

interface Venue {
  id: number;
  name: string;
}

interface EventType {
  id: number;
  name: string;
}

type ViewMode = "calendar" | "list";
type TimeFilter = "all" | "upcoming" | "past" | "today";

const ALL_STATUSES = ["all", "draft", "quoted", "confirmed", "completed", "cancelled"] as const;

export default function CalendarPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [selectedVenue, setSelectedVenue] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");

  const [isMobile, setIsMobile] = useState(false);
  const [showAvailabilityChecker, setShowAvailabilityChecker] = useState(false);
  const [addEventDefaults, setAddEventDefaults] = useState<{ venueId?: number; startDate?: string; endDate?: string } | null>(null);
  const [showAddEvent, setShowAddEvent] = useState(false);

  // Mobile detection for default view
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setViewMode("list");
      }
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventsRes, venuesRes, typesRes] = await Promise.all([
        eventsApi.list(),
        mastersApi.venues.list(),
        mastersApi.eventTypes.list().catch(() => ({ data: [] })),
      ]);

      const eventsList = eventsRes.data?.data || eventsRes.data || [];
      const venuesList = venuesRes.data?.data || venuesRes.data || [];
      const typesList = typesRes.data?.data || typesRes.data || [];

      setEvents(eventsList);
      setVenues(venuesList);
      setEventTypes(typesList);
    } catch (error) {
      console.error("Failed to load data", error);
    } finally {
      setLoading(false);
    }
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isPastEvent = (event: Event) => {
    const d = new Date(event.start_date || event.event_date || "");
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const isTodayEvent = (event: Event) => {
    const d = new Date(event.start_date || event.event_date || "");
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  };

  const isUpcomingEvent = (event: Event) => {
    const d = new Date(event.start_date || event.event_date || "");
    d.setHours(0, 0, 0, 0);
    return d > today;
  };

  // Filtered events for the current month view
  const monthEvents = useMemo(() => {
    return events.filter((event) => {
      const eventDate = new Date(event.start_date || event.event_date || "");
      return (
        eventDate.getMonth() === currentDate.getMonth() &&
        eventDate.getFullYear() === currentDate.getFullYear()
      );
    });
  }, [events, currentDate]);

  // All filtered events (for list view + stats)
  const filteredEvents = useMemo(() => {
    let filtered = [...events];

    // Month filter (only apply in calendar view, list shows all)
    if (viewMode === "calendar") {
      filtered = monthEvents;
    }

    // Venue filter
    if (selectedVenue) {
      filtered = filtered.filter((e) => String(e.venue_id) === selectedVenue);
    }

    // Status filter
    if (selectedStatus !== "all") {
      filtered = filtered.filter((e) => e.status === selectedStatus);
    }

    // Event type filter
    if (selectedType) {
      filtered = filtered.filter(
        (e) => e.event_type === selectedType || e.type === selectedType
      );
    }

    // Client search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((e) => {
        const clientName = e.client?.name || "";
        const eventName = e.name || e.event_name || "";
        return (
          clientName.toLowerCase().includes(q) ||
          eventName.toLowerCase().includes(q)
        );
      });
    }

    // Time filter
    if (timeFilter === "past") {
      filtered = filtered.filter(isPastEvent);
    } else if (timeFilter === "upcoming") {
      filtered = filtered.filter(isUpcomingEvent);
    } else if (timeFilter === "today") {
      filtered = filtered.filter(isTodayEvent);
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.start_date || a.event_date || "").getTime();
      const dateB = new Date(b.start_date || b.event_date || "").getTime();
      return dateA - dateB;
    });
  }, [
    events,
    monthEvents,
    viewMode,
    selectedVenue,
    selectedStatus,
    selectedType,
    searchQuery,
    timeFilter,
  ]);

  const stats = useMemo(() => {
    const base = viewMode === "calendar" ? monthEvents : events;
    return {
      total: base.length,
      upcoming: base.filter(isUpcomingEvent).length,
      past: base.filter(isPastEvent).length,
      today: base.filter(isTodayEvent).length,
    };
  }, [events, monthEvents, viewMode, currentDate]);

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getEventsForDate = (day: number): Event[] => {
    return filteredEvents.filter((event) => {
      const eventDate = new Date(event.start_date || event.event_date || "");
      return eventDate.getDate() === day;
    });
  };

  const monthName = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const shortMonthName = currentDate.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  const getEventTimeClass = (event: Event) => {
    if (isTodayEvent(event)) return "bg-amber-100 text-amber-700 border-amber-200";
    if (isPastEvent(event)) return "bg-red-50 text-red-700 border-red-200";
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  };

  const getEventDotColor = (event: Event) => {
    if (isTodayEvent(event)) return "bg-amber-500";
    if (isPastEvent(event)) return "bg-red-500";
    return "bg-emerald-500";
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-emerald-100 text-emerald-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "quoted":
        return "bg-violet-100 text-violet-700";
      case "draft":
        return "bg-stone-100 text-stone-600";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  const resetFilters = () => {
    setSelectedVenue("");
    setSelectedStatus("all");
    setSelectedType("");
    setSearchQuery("");
    setTimeFilter("all");
  };

  const hasActiveFilters =
    selectedVenue || selectedStatus !== "all" || selectedType || searchQuery || timeFilter !== "all";

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const shortDayNames = ["S", "M", "T", "W", "T", "F", "S"];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-stone-800">
              Event Calendar
            </h1>
            <p className="text-xs md:text-sm text-stone-500 mt-0.5 hidden sm:block">
              Manage and track your events
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Availability Checker Toggle */}
            <button
              onClick={() => setShowAvailabilityChecker((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                showAvailabilityChecker
                  ? "bg-amber-100 text-amber-700 border border-amber-300"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
              title="Check venue availability"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Availability</span>
            </button>

            {/* View Toggle */}
            <div className="flex bg-stone-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode("calendar")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "calendar"
                    ? "bg-white shadow-sm text-amber-600"
                    : "text-stone-500 hover:text-stone-700"
                }`}
                title="Calendar view"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "list"
                    ? "bg-white shadow-sm text-amber-600"
                    : "text-stone-500 hover:text-stone-700"
                }`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleToday}
              className="px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap"
            >
              Today
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-4 gap-2 md:gap-3">
          <div className="bg-white border border-stone-200 rounded-xl p-2 md:p-3 text-center">
            <div className="text-lg md:text-xl font-bold text-stone-800">{stats.total}</div>
            <div className="text-[10px] md:text-xs text-stone-500 font-medium uppercase tracking-wide">
              Total
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2 md:p-3 text-center">
            <div className="text-lg md:text-xl font-bold text-emerald-700">{stats.upcoming}</div>
            <div className="text-[10px] md:text-xs text-emerald-600 font-medium uppercase tracking-wide">
              Upcoming
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-2 md:p-3 text-center">
            <div className="text-lg md:text-xl font-bold text-red-700">{stats.past}</div>
            <div className="text-[10px] md:text-xs text-red-600 font-medium uppercase tracking-wide">
              Past
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 md:p-3 text-center">
            <div className="text-lg md:text-xl font-bold text-amber-700">{stats.today}</div>
            <div className="text-[10px] md:text-xs text-amber-600 font-medium uppercase tracking-wide">
              Today
            </div>
          </div>
        </div>

        {/* Availability Checker Panel */}
        {showAvailabilityChecker && (
          <AvailabilityChecker
            onCreateEvent={(venueId, _venueName, startDate, endDate) => {
              setAddEventDefaults({ venueId, startDate, endDate });
              setShowAddEvent(true);
            }}
          />
        )}

        {/* Search & Filter Bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events or clients..."
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`relative p-2.5 rounded-lg border transition-colors ${
              hasActiveFilters || showFilters
                ? "border-amber-400 bg-amber-50 text-amber-600"
                : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
            }`}
          >
            <Filter className="w-4 h-4" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                !
              </span>
            )}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-stone-800 text-sm">Filters</h3>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-1 hover:bg-stone-100 rounded"
                >
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Time Filter */}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  Time Period
                </label>
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
                >
                  <option value="all">All Events</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="today">Today</option>
                  <option value="past">Past</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
                >
                  {ALL_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Venue Filter */}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  Venue
                </label>
                <select
                  value={selectedVenue}
                  onChange={(e) => setSelectedVenue(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
                >
                  <option value="">All Venues</option>
                  {venues.map((venue) => (
                    <option key={venue.id} value={String(venue.id)}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Event Type Filter */}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  Event Type
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
                >
                  <option value="">All Types</option>
                  {eventTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Navigation (shared) */}
        <div className="flex items-center justify-between bg-white border border-stone-200 rounded-xl px-3 py-2 md:px-4 md:py-3">
          <button
            onClick={handlePrevMonth}
            className="p-1.5 md:p-2 hover:bg-stone-100 rounded-lg transition-colors active:bg-stone-200"
          >
            <ChevronLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div className="text-center">
            <h2 className="text-base md:text-lg font-bold text-stone-800">
              {isMobile ? shortMonthName : monthName}
            </h2>
            <p className="text-[10px] md:text-xs text-stone-500">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={handleNextMonth}
            className="p-1.5 md:p-2 hover:bg-stone-100 rounded-lg transition-colors active:bg-stone-200"
          >
            <ChevronRight className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        {/* CALENDAR VIEW */}
        {viewMode === "calendar" && (
          <div className="bg-white border border-stone-200 rounded-xl p-2 md:p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1 md:mb-2">
              {(isMobile ? shortDayNames : dayNames).map((day) => (
                <div
                  key={day}
                  className="text-center font-semibold text-stone-600 py-1 md:py-2 text-[10px] md:text-xs"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {emptyDays.map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square bg-stone-50 rounded-lg" />
              ))}

              {days.map((day) => {
                const dayEvents = getEventsForDate(day);
                const isTodayDay =
                  day === today.getDate() &&
                  currentDate.getMonth() === today.getMonth() &&
                  currentDate.getFullYear() === today.getFullYear();

                const pastEvents = dayEvents.filter(isPastEvent);
                const upcomingEvents = dayEvents.filter(isUpcomingEvent);
                const todayEvents = dayEvents.filter(isTodayEvent);

                return (
                  <div
                    key={day}
                    className={`aspect-square rounded-lg border p-1 md:p-1.5 flex flex-col transition-all ${
                      isTodayDay
                        ? "border-amber-400 bg-amber-50"
                        : dayEvents.length > 0
                        ? "border-stone-200 bg-white hover:border-stone-300"
                        : "border-stone-100 bg-white hover:border-stone-200"
                    }`}
                  >
                    <div
                      className={`text-[10px] md:text-xs font-semibold mb-0.5 ${
                        isTodayDay ? "text-amber-700" : "text-stone-700"
                      }`}
                    >
                      {day}
                    </div>

                    {/* Event dots - compact indicator */}
                    <div className="flex-1 flex flex-wrap content-end gap-[2px] md:gap-1">
                      {todayEvents.slice(0, 2).map((event) => (
                        <Link
                          key={event.id}
                          to={`/events/${event.id}`}
                          className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-amber-500"
                          title={`${event.name || event.event_name} (Today)`}
                        />
                      ))}
                      {upcomingEvents.slice(0, 3).map((event) => (
                        <Link
                          key={event.id}
                          to={`/events/${event.id}`}
                          className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-emerald-500"
                          title={`${event.name || event.event_name} (Upcoming)`}
                        />
                      ))}
                      {pastEvents.slice(0, 3).map((event) => (
                        <Link
                          key={event.id}
                          to={`/events/${event.id}`}
                          className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-red-400"
                          title={`${event.name || event.event_name} (Past)`}
                        />
                      ))}
                      {dayEvents.length > 5 && (
                        <span className="text-[8px] md:text-[10px] text-stone-400 leading-none">
                          +{dayEvents.length - 5}
                        </span>
                      )}
                    </div>

                    {/* Mobile: show count badge */}
                    {dayEvents.length > 0 && (
                      <div className="hidden sm:block mt-0.5">
                        <div className="text-[9px] md:text-[10px] text-stone-500 truncate">
                          {dayEvents.length} event{dayEvents.length > 1 ? "s" : ""}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-3 md:mt-4 flex flex-wrap gap-3 md:gap-4 text-[10px] md:text-xs justify-center">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-stone-600">Upcoming</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-stone-600">Today</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-stone-600">Past</span>
              </div>
            </div>
          </div>
        )}

        {/* LIST VIEW */}
        {viewMode === "list" && (
          <div className="space-y-2 md:space-y-3">
            {filteredEvents.length > 0 ? (
              filteredEvents.map((event) => {
                const eventDate = new Date(event.start_date || event.event_date || "");
                const displayName = event.name || event.event_name || "Untitled Event";
                const isPast = isPastEvent(event);
                const isTodayE = isTodayEvent(event);
                const isUpcoming = isUpcomingEvent(event);

                return (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className={`block bg-white border rounded-xl p-3 md:p-4 transition-all hover:shadow-md ${
                      isPast
                        ? "border-red-200 hover:border-red-300"
                        : isTodayE
                        ? "border-amber-300 hover:border-amber-400"
                        : "border-stone-200 hover:border-emerald-300"
                    }`}
                  >
                    {/* Mobile Layout */}
                    <div className="flex items-start gap-3">
                      {/* Date Block */}
                      <div
                        className={`flex-shrink-0 w-12 md:w-14 rounded-lg p-1.5 md:p-2 text-center ${
                          isPast
                            ? "bg-red-50"
                            : isTodayE
                            ? "bg-amber-50"
                            : "bg-emerald-50"
                        }`}
                      >
                        <div
                          className={`text-[10px] md:text-xs font-semibold uppercase ${
                            isPast
                              ? "text-red-600"
                              : isTodayE
                              ? "text-amber-600"
                              : "text-emerald-600"
                          }`}
                        >
                          {eventDate.toLocaleDateString("en-US", { month: "short" })}
                        </div>
                        <div
                          className={`text-base md:text-lg font-bold ${
                            isPast
                              ? "text-red-700"
                              : isTodayE
                              ? "text-amber-700"
                              : "text-emerald-700"
                          }`}
                        >
                          {eventDate.getDate()}
                        </div>
                        <div className="text-[9px] md:text-[10px] text-stone-500">
                          {eventDate.toLocaleDateString("en-US", { weekday: "short" })}
                        </div>
                      </div>

                      {/* Event Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-stone-800 text-sm md:text-base truncate">
                            {displayName}
                          </h4>
                          <span
                            className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium ${getStatusBadge(
                              event.status
                            )}`}
                          >
                            {event.status}
                          </span>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] md:text-xs text-stone-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {event.client?.name || "No client"}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.venue?.name || "No venue"}
                          </span>
                          {event.guest_count ? (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {event.guest_count} guests
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-1.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isPast && (
                              <span className="inline-flex items-center gap-1 text-[10px] md:text-xs text-red-600 font-medium">
                                <Clock className="w-3 h-3" />
                                Past
                              </span>
                            )}
                            {isTodayE && (
                              <span className="inline-flex items-center gap-1 text-[10px] md:text-xs text-amber-600 font-medium">
                                <CalendarDays className="w-3 h-3" />
                                Today
                              </span>
                            )}
                            {isUpcoming && (
                              <span className="inline-flex items-center gap-1 text-[10px] md:text-xs text-emerald-600 font-medium">
                                <CheckCircle2 className="w-3 h-3" />
                                Upcoming
                              </span>
                            )}
                          </div>
                          <span className="text-sm md:text-base font-bold text-stone-700">
                            ₹{event.total_amount?.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="text-center py-12 bg-white border border-stone-200 rounded-xl">
                <CalendarIcon className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 font-medium text-sm">
                  No events found
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  Try adjusting your filters or search
                </p>
              </div>
            )}
          </div>
        )}

        {/* Selected Day Events Detail (Calendar view only) */}
        {viewMode === "calendar" && filteredEvents.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-xl p-4 md:p-5">
            <h3 className="text-sm md:text-base font-bold text-stone-800 mb-3">
              All Events in {monthName}{" "}
              <span className="text-stone-500 font-normal">
                ({filteredEvents.length})
              </span>
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredEvents.map((event) => {
                const eventDate = new Date(event.start_date || event.event_date || "");
                const displayName = event.name || event.event_name || "Untitled Event";
                const isPast = isPastEvent(event);

                return (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                      isPast
                        ? "border-red-100 bg-red-50/50 hover:bg-red-50"
                        : "border-stone-100 hover:bg-stone-50"
                    }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        isPast ? "bg-red-400" : "bg-emerald-400"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-stone-700 truncate">
                          {displayName}
                        </span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full ${getStatusBadge(
                            event.status
                          )}`}
                        >
                          {event.status}
                        </span>
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        {eventDate.toLocaleDateString("en-IN")} ·{" "}
                        {event.client?.name} · {event.venue?.name || "No venue"}
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-stone-600">
                      ₹{event.total_amount?.toLocaleString("en-IN")}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add Event Modal — triggered from Availability Checker */}
      <AddEventModal
        isOpen={showAddEvent}
        onClose={() => { setShowAddEvent(false); setAddEventDefaults(null); }}
        onEventAdded={() => { setShowAddEvent(false); setAddEventDefaults(null); loadData(); }}
        defaultVenueId={addEventDefaults?.venueId}
        defaultStartDate={addEventDefaults?.startDate}
      />
    </DashboardLayout>
  );
}

